import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useFragment as getFragmentData } from '@/graphql';
import type {
    AttributeAssetFieldsFragment,
    AttributeEntryFieldsFragment,
    AttributesPageQuery,
    AttributesPageQueryVariables,
    Permission,
} from '@/graphql/types';
import {
    AttributeAssetFieldsFragmentDoc,
    AttributeEntryFieldsFragmentDoc,
    AttributesPageDocument,
} from '@/graphql/types';
import type { AttributeAsset, AttributeEntityType, AttributeEntry } from '../contracts';
import { formatAttributeLabel } from '../builtInLabels';
import {
    formatAttributeEntityType,
    normalizeAttributeValueType,
    toAttributesEntityGraphQLValue,
} from '../contracts';

function adaptAsset(asset: AttributeAssetFieldsFragment): AttributeAsset {
    return {
        id: asset.id,
        url: asset.url,
        name: asset.name ?? null,
        caption: asset.caption,
        sortOrder: asset.sortOrder ?? null,
    };
}

export function adaptAttributeEntry(entry: AttributeEntryFieldsFragment): AttributeEntry {
    const valueType = normalizeAttributeValueType(String(entry.valueType));
    const base = {
        definitionId: entry.definitionId,
        key: entry.key,
        label: formatAttributeLabel(entry),
        valueType,
        updatedAt: entry.updatedAt ?? null,
    };

    if (entry.__typename === 'TextAttributeEntry') {
        return {
            ...base,
            valueType: 'text',
            textValue: entry.textValue,
        };
    }

    if (entry.__typename === 'ImageAttributeEntry') {
        return {
            ...base,
            valueType: 'image',
            image: entry.image
                ? adaptAsset(getFragmentData(AttributeAssetFieldsFragmentDoc, entry.image))
                : null,
        };
    }

    if (entry.__typename === 'ImageListAttributeEntry') {
        return {
            ...base,
            valueType: 'image_list',
            imageList: getFragmentData(AttributeAssetFieldsFragmentDoc, entry.imageList).map(
                adaptAsset
            ),
        };
    }

    const unknownEntry = entry as { __typename?: unknown };
    throw new Error(`Unknown Attributes entry typename: ${String(unknownEntry.__typename)}`);
}

interface UseAttributesDataArgs {
    entityType: AttributeEntityType;
    entityId: string;
    keys?: string[];
}

export function useAttributesData({ entityType, entityId, keys }: UseAttributesDataArgs) {
    const variables = useMemo<AttributesPageQueryVariables>(
        () => ({
            entityType: toAttributesEntityGraphQLValue(
                entityType
            ) as AttributesPageQueryVariables['entityType'],
            entityId,
            keys,
            includeMeasuringPoint: entityType === 'measuring_point',
            includeProject: entityType === 'project',
        }),
        [entityId, entityType, keys]
    );

    const queryResult = useQuery<AttributesPageQuery, AttributesPageQueryVariables>(
        AttributesPageDocument,
        {
            variables,
            fetchPolicy: 'cache-and-network',
        }
    );

    const entries = useMemo(
        () =>
            getFragmentData(
                AttributeEntryFieldsFragmentDoc,
                queryResult.data?.attributesByEntity ?? []
            ).map(adaptAttributeEntry),
        [queryResult.data?.attributesByEntity]
    );
    const { data } = queryResult;
    const measuringPointById = data?.measuringPointById;
    const projectById = data?.projectById;
    const entityLabel =
        measuringPointById?.name ?? projectById?.name ?? formatAttributeEntityType(entityType);
    let entityPermission: Permission | null = null;

    if (entityType === 'measuring_point') {
        entityPermission = measuringPointById?.permission ?? null;
    } else if (entityType === 'project') {
        entityPermission = projectById?.permission ?? null;
    }

    return {
        ...queryResult,
        entries,
        entityLabel,
        entityPermission,
    };
}
